import { Link } from "react-router-dom";
import DashboardLayout from "@/pages/dashboard/components/DashboardLayout";
import { useAuth } from "@/hooks/useAuth";
import {
  favoriteCleaners,
  pastBookings,
  recentActivity,
  upcomingBookings,
} from "@/mocks/dashboard";

const toneStyles = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-700",
  secondary: "bg-secondary-100 text-secondary-800",
};

function formatDate(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export default function DashboardOverviewPage() {
  const { user } = useAuth();
  const next = upcomingBookings[0];

  const stats = [
    {
      label: "Upcoming visits",
      value: upcomingBookings.length,
      icon: "ri-calendar-event-line",
      tint: "bg-primary-100 text-primary-700",
    },
    {
      label: "Completed cleans",
      value: pastBookings.length + 12,
      icon: "ri-check-double-line",
      tint: "bg-accent-100 text-accent-700",
    },
    {
      label: "Favorite cleaners",
      value: favoriteCleaners.length,
      icon: "ri-heart-3-line",
      tint: "bg-secondary-100 text-secondary-800",
    },
    {
      label: "Saved this year",
      value: "$248",
      icon: "ri-line-chart-line",
      tint: "bg-primary-100 text-primary-700",
    },
  ];

  return (
    <DashboardLayout
      title={`Welcome back, ${user?.firstName ?? "friend"}`}
      subtitle="Here's what's lined up for your home."
      action={
        <Link
          to="/booking"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 pl-5 pr-2 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
        >
          Book a clean
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50">
            <i className="ri-arrow-right-up-line text-primary-600 text-sm"></i>
          </span>
        </Link>
      }
    >
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="bg-background-50 border border-background-200 rounded-xl p-4 md:p-5"
            >
              <div className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg ${s.tint}`}>
                <i className={`${s.icon} text-base md:text-lg`}></i>
              </div>
              <p className="mt-2 md:mt-3 text-xl md:text-2xl font-heading font-semibold text-foreground-950">
                {s.value}
              </p>
              <p className="text-xs text-foreground-600 mt-0.5 leading-tight">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Next visit */}
          <div className="lg:col-span-2 bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-background-200 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-primary-700 uppercase tracking-wider">
                  Your next visit
                </p>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mt-1">
                  {next.service}
                </h2>
              </div>
              <span className="inline-flex items-center gap-1.5 bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-600"></span>
                Confirmed
              </span>
            </div>
            <div className="p-5 md:p-6 flex flex-col md:flex-row gap-5 md:gap-6">
              <div className="flex items-center gap-4">
                <img
                  src={next.cleanerImage}
                  alt={next.cleanerName}
                  className="w-20 h-20 rounded-2xl object-cover object-top"
                />
                <div>
                  <p className="text-xs text-foreground-600">Your cleaner</p>
                  <p className="text-base font-medium text-foreground-950">{next.cleanerName}</p>
                  <p className="text-xs text-foreground-600 mt-1 flex items-center gap-1">
                    <i className="ri-star-fill text-secondary-500"></i> 4.96 · 612 jobs
                  </p>
                </div>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-4 md:border-l md:border-background-200 md:pl-6">
                <div>
                  <p className="text-xs text-foreground-600">Date</p>
                  <p className="text-sm font-medium text-foreground-950 mt-0.5">
                    {formatDate(next.date)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-foreground-600">Time</p>
                  <p className="text-sm font-medium text-foreground-950 mt-0.5">{next.time}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-600">Duration</p>
                  <p className="text-sm font-medium text-foreground-950 mt-0.5">{next.duration}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-600">Total</p>
                  <p className="text-sm font-medium text-foreground-950 mt-0.5">${next.total}</p>
                </div>
              </div>
            </div>
            <div className="px-5 md:px-6 pb-5 md:pb-6 flex flex-wrap items-center gap-2 md:gap-3">
              <button className="inline-flex items-center gap-1.5 bg-foreground-950 hover:bg-foreground-900 text-background-50 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap">
                <i className="ri-message-3-line"></i> Message Maya
              </button>
              <button className="inline-flex items-center gap-1.5 bg-background-100 hover:bg-background-200 text-foreground-800 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap">
                <i className="ri-calendar-line"></i> Reschedule
              </button>
              <Link
                to="/dashboard/bookings"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800 ml-auto whitespace-nowrap"
              >
                View all bookings <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          {/* Activity */}
          <div className="bg-background-50 border border-background-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                Recent activity
              </h3>
              <button className="text-xs text-foreground-600 hover:text-foreground-900 whitespace-nowrap">
                See all
              </button>
            </div>
            <ul className="space-y-4">
              {recentActivity.map((a) => (
                <li key={a.id} className="flex items-start gap-3">
                  <span
                    className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${toneStyles[a.tone]}`}
                  >
                    <i className={`${a.icon} text-sm`}></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-950">{a.title}</p>
                    <p className="text-xs text-foreground-600 leading-relaxed">{a.description}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{a.time}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Favorite cleaners snapshot */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                Your favorite cleaners
              </h3>
              <p className="text-xs text-foreground-600 mt-0.5">
                People you've requested more than once.
              </p>
            </div>
            <Link
              to="/dashboard/cleaners"
              className="text-sm font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
            >
              View all <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {favoriteCleaners.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-4 p-4 rounded-xl bg-background-100"
              >
                <img
                  src={c.image}
                  alt={c.name}
                  className="w-14 h-14 rounded-xl object-cover object-top flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground-950 truncate">{c.name}</p>
                  <p className="text-xs text-foreground-600">{c.role}</p>
                  <p className="text-xs text-foreground-600 mt-1 flex items-center gap-1">
                    <i className="ri-star-fill text-secondary-500"></i> {c.rating} · {c.bookings}{" "}
                    visits
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}